import { useCallback, type KeyboardEvent } from 'react';
import type { DirEntry } from '@slate/core';
import { useSpeedSearch } from './useSpeedSearch';

/**
 * Minimal shape the speed-search hook needs from each visible tree node. The
 * FileTree's `VisibleNode` is structurally compatible (it has `entry` +
 * `parentPath`), so callers pass their flattened `visible` array directly.
 */
export interface SpeedSearchNode {
  entry: DirEntry;
  /** Path of the parent directory, or null for root entries. */
  parentPath: string | null;
}

export { SPEED_SEARCH_IDLE_MS } from './useSpeedSearch';

/**
 * Result of feeding a keydown to {@link useTreeSpeedSearch.handleKey}. FileTree
 * uses this to decide whether the key was consumed and where (if anywhere) to
 * move focus — the hook never touches FileTree's focus state itself.
 */
export type SpeedSearchKeyResult =
  /** The key was not relevant to speed search — fall through to normal nav. */
  | { handled: false }
  /**
   * Speed search consumed the key (the caller already `preventDefault`ed via
   * the hook). `focusPath`, when set, is the row FileTree should focus next
   * (Up/Down match cycling). The query-change focus jump is delivered
   * separately via the `onJumpToMatch` callback so it can also fire from
   * Backspace edits and respects the "keep focus if still matching" rule.
   */
  | { handled: true; focusPath?: string };

export interface UseTreeSpeedSearchOptions {
  /** The flattened, currently-visible nodes in linear (display) order. */
  visible: readonly SpeedSearchNode[];
  /** The currently focused row's path (FileTree owns this). */
  focusedPath: string | null;
  /**
   * Called when the query changes (type / backspace) and focus should jump.
   * FileTree wires this to `focusRow`. The hook only calls it when the rules in
   * spec §5 say focus should move (focused row no longer matches → first match).
   */
  onJumpToMatch: (path: string) => void;
}

export interface UseTreeSpeedSearch {
  /** Current query text. `''` means inactive. */
  query: string;
  /** True while a non-empty query is in effect. */
  active: boolean;
  /** Paths of matching rows, in visible order. */
  matches: string[];
  /** Set form of {@link matches} for O(1) membership checks during render. */
  matchSet: Set<string>;
  /** The matched `[start, end)` range for a row's NAME, or null if no match. */
  rangeFor: (name: string) => [number, number] | null;
  /**
   * Feed a keydown event. Returns whether speed search consumed it and where
   * focus should move. Calls `e.preventDefault()` itself when it consumes.
   */
  handleKey: (e: KeyboardEvent<HTMLElement>) => SpeedSearchKeyResult;
  /** Clear the query + cancel the idle timer (Esc, blur, programmatic exit). */
  reset: () => void;
}

/**
 * IntelliJ-style speed search for the file tree. Owns the query string + the
 * idle-reset timer and derives the match set from the supplied `visible` list.
 * It deliberately does NOT own focus: it returns intents/handlers and FileTree
 * applies the focus moves through its existing `focusRow` / `setFocusedPath`.
 *
 * Focus rules (spec §5):
 *   - On query change (type / backspace): keep focus if the focused row still
 *     matches; else jump to the first match; else leave focus put (no matches).
 *     Implemented in an effect keyed on `query`, NOT on `visible`, so an
 *     expand/collapse that only changes `visible` never re-jumps.
 *   - Up/Down while active + matches exist: cycle to the next/previous match
 *     with wrap-around (returned via `handleKey`).
 */
export function useTreeSpeedSearch({
  visible,
  focusedPath,
  onJumpToMatch,
}: UseTreeSpeedSearchOptions): UseTreeSpeedSearch {
  const inner = useSpeedSearch<SpeedSearchNode>({
    items: visible,
    getId: (n) => n.entry.path,
    getLabel: (n) => n.entry.name,
    focusedId: focusedPath,
    onJumpToMatch,
  });
  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLElement>): SpeedSearchKeyResult => {
      const r = inner.handleKey(e);
      return r.handled ? { handled: true, focusPath: r.focusId } : { handled: false };
    },
    [inner],
  );
  return {
    query: inner.query,
    active: inner.active,
    matches: inner.matches,
    matchSet: inner.matchSet,
    rangeFor: inner.rangeFor,
    handleKey,
    reset: inner.reset,
  };
}
